/**
 * Manager dashboard presets — the per-profile SECONDARY KPI row and the
 * business-area card order.
 *
 * Kept out of `screens/ManagementDashboardScreen.js` so the table stays a pure,
 * React-Native-free module the Jest suite can assert against directly.
 *
 * The profile itself comes from `resolveDashboardProfile` (placeType → role →
 * finance visibility → hospitality) in `helpers/managementAccess.js`. What it
 * may and may not do:
 *
 *  - MAY re-order the business-area cards so the store's own vertical leads.
 *  - MAY append one extra KPI row (`focusKey` labels it) BELOW the management
 *    overview.
 *  - MUST NOT rename the screen. The hero always reads `mgmt_dash_hero_title`
 *    ("Management dashboard"), because in manager mode this IS the manager's
 *    dashboard whatever vertical the store runs. A service-type store used to
 *    have the hero replaced by "Service dashboard" and the management KPI
 *    quartet replaced by work-order counters, which made manager mode look and
 *    read like the technician's screen.
 *  - MUST NOT hide an area: every preset lists all five areas, only the order
 *    differs. Access is decided by `canAccess`, never by the profile.
 */

/** i18n key of the Manager dashboard hero title. Never profile-dependent. */
export const MANAGEMENT_HERO_TITLE_KEY = 'mgmt_dash_hero_title';

/** Every business area a preset must order (all of them, always). */
export const DASHBOARD_AREAS = ['hospitality', 'service', 'crm', 'finance', 'logistics'];

/**
 * @typedef {object} DashboardPreset
 * @property {?string} focusKey  i18n key labelling the extra KPI row, `null` for the default profile.
 * @property {string} [focusIcon] MaterialCommunityIcons name for that row's section heading.
 * @property {string[]} areas    Business-area card order (a permutation of {@link DASHBOARD_AREAS}).
 */

/** @type {Record<string, DashboardPreset>} */
export const DASHBOARD_PROFILE_PRESETS = {
    hospitality: {focusKey: null, areas: ['hospitality', 'service', 'crm', 'finance', 'logistics']},
    finance: {focusKey: 'mgmt_dash_profile_finance', focusIcon: 'finance', areas: ['finance', 'hospitality', 'crm', 'logistics', 'service']},
    service: {focusKey: 'mgmt_dash_profile_service', focusIcon: 'tools', areas: ['service', 'crm', 'hospitality', 'finance', 'logistics']},
    warehouse: {focusKey: 'mgmt_dash_profile_warehouse', focusIcon: 'warehouse', areas: ['logistics', 'hospitality', 'finance', 'service', 'crm']},
    crm: {focusKey: 'mgmt_dash_profile_crm', focusIcon: 'account-tie-outline', areas: ['crm', 'hospitality', 'finance', 'service', 'logistics']},
};

/**
 * Resolve a dashboard profile key to its preset, falling back to the default
 * hospitality layout for an unknown/absent profile.
 *
 * @param {string} [profile] Profile key from `resolveDashboardProfile`.
 * @returns {DashboardPreset} The preset to render with (never undefined).
 */
export const getDashboardPreset = (profile) => DASHBOARD_PROFILE_PRESETS[profile] || DASHBOARD_PROFILE_PRESETS.hospitality;
